import { useRef, useState, useCallback, useEffect } from "react";
import api from "@/lib/api";

const MAX_SECONDS = 180; // 3 Minuten
const WARN_SECONDS = 10; // 10 s vor Ende warnen

/**
 * Universelles Sprachdiktat: nimmt Audio auf (MediaRecorder), visualisiert die
 * Lautstärke (Web Audio Analyser), stoppt automatisch nach 3 Minuten (Warnung
 * 10 s vorher), transkribiert per Whisper und lässt den Text per KI nachbearbeiten.
 *
 * Zustände: recording | processing | done(result) | error
 */
export function useVoiceDictation({ onError } = {}) {
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [volume, setVolume] = useState(0); // 0..1
  const [secondsLeft, setSecondsLeft] = useState(MAX_SECONDS);
  const [result, setResult] = useState(null); // { raw, corrected }

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const timerRef = useRef(null);
  const chunksRef = useRef([]);
  const cancelledRef = useRef(false);
  const mimeRef = useRef("audio/webm");

  const supported =
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof window !== "undefined" &&
    !!window.MediaRecorder;

  const cleanup = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
    rafRef.current = null;
    timerRef.current = null;
    try { audioCtxRef.current?.close(); } catch { /* ignore */ }
    audioCtxRef.current = null;
    analyserRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setVolume(0);
  }, []);

  const processAudio = useCallback(async () => {
    const chunks = chunksRef.current;
    chunksRef.current = [];
    if (!chunks.length) { setProcessing(false); return; }
    const mime = mimeRef.current || "audio/webm";
    const blob = new Blob(chunks, { type: mime });
    if (blob.size < 800) { setProcessing(false); return; } // zu kurz / kein Ton
    const ext = mime.includes("mpeg") ? "mp3" : mime.includes("ogg") ? "ogg" : mime.includes("wav") ? "wav" : "webm";
    try {
      const form = new FormData();
      form.append("file", blob, `aufnahme.${ext}`);
      const tRes = await api.post("/voice/transcribe", form, { headers: { "Content-Type": "multipart/form-data" } });
      const transcript = (tRes.data?.transcript || "").trim();
      if (!transcript) {
        setProcessing(false);
        onError?.({ type: "empty" });
        return;
      }
      const rRes = await api.post("/voice/refine", { text: transcript });
      setResult({
        raw: rRes.data?.raw || transcript,
        corrected: rRes.data?.corrected || transcript,
        refined: rRes.data?.refined !== false,
      });
    } catch (e) {
      onError?.({ type: "process", error: e });
    } finally {
      setProcessing(false);
    }
  }, [onError]);

  const stop = useCallback(() => {
    if (!recording) return;
    setRecording(false);
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== "inactive") {
      try { mr.stop(); } catch { /* ignore */ }
    }
    cleanup();
  }, [recording, cleanup]);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    setRecording(false);
    setProcessing(false);
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== "inactive") { try { mr.stop(); } catch { /* ignore */ } }
    chunksRef.current = [];
    cleanup();
  }, [cleanup]);

  const start = useCallback(async () => {
    if (!supported || recording) return;
    cancelledRef.current = false;
    setResult(null);
    setSecondsLeft(MAX_SECONDS);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Lautstärke-Analyse
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      analyserRef.current = analyser;
      const buf = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
        const rms = Math.sqrt(sum / buf.length);
        setVolume(Math.min(1, rms * 3));
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);

      // Aufnahme
      let mimeType = "";
      if (window.MediaRecorder.isTypeSupported?.("audio/webm;codecs=opus")) mimeType = "audio/webm;codecs=opus";
      else if (window.MediaRecorder.isTypeSupported?.("audio/webm")) mimeType = "audio/webm";
      const mr = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      mimeRef.current = mr.mimeType || mimeType || "audio/webm";
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        if (cancelledRef.current) { chunksRef.current = []; return; }
        setProcessing(true);
        processAudio();
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);

      // Countdown / Auto-Stopp
      timerRef.current = setInterval(() => {
        setSecondsLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            // Auto-Stopp
            setRecording(false);
            const rec = mediaRecorderRef.current;
            if (rec && rec.state !== "inactive") { try { rec.stop(); } catch { /* ignore */ } }
            cleanup();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (e) {
      cleanup();
      setRecording(false);
      onError?.({ type: "permission", error: e });
    }
  }, [supported, recording, processAudio, cleanup, onError]);

  useEffect(() => () => { cancelledRef.current = true; cleanup(); }, [cleanup]);

  const warning = recording && secondsLeft <= WARN_SECONDS;
  const clearResult = useCallback(() => setResult(null), []);

  return { supported, recording, processing, volume, secondsLeft, warning, result, start, stop, cancel, clearResult };
}
